import { useQuery } from '@tanstack/react-query';
import { Loader2, Trophy, Users } from 'lucide-react';
import { fetchEquipesPubliques } from '../services/api';

/**
 * Les équipes du club, vues du village.
 *
 * C'est ainsi qu'un parent trouve celle de son enfant avant de venir
 * s'inscrire, et qu'un joueur nouveau dans la commune sait à qui s'adresser.
 *
 * Chaque équipe est une carte, et chaque carte sépare ceux qui encadrent de
 * ceux qui jouent : c'est la lecture d'une affiche de club, et elle répond à
 * la question qu'on se pose vraiment — qui s'occupe des U13 ?
 */
export default function NosEquipes() {
  const { data, isPending } = useQuery({
    queryKey: ['equipes-publiques'],
    queryFn: fetchEquipesPubliques,
  });

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const equipes: any[] = data?.equipes ?? [];

  if (!data?.ouverte) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Users className="w-10 h-10 mx-auto text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Les équipes ne sont pas publiées
        </h1>
        <p className="text-sm text-gray-500">
          Le club ne montre pas la composition de ses équipes sur son site.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Trophy className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            {data.titre}
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            {data.texte || `Saison ${data.saison}`}
          </p>
        </div>
      </div>

      {equipes.length === 0 ? (
        <div className="card lg:p-6 text-center text-sm text-gray-500">
          Aucune équipe composée pour l'instant. Revenez en début de saison.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {equipes.map((equipe, rang) => (
            <article key={rang} className="card lg:p-6 space-y-4">
              <header>
                <h2 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{equipe.nom}</h2>
                <p className="text-sm text-gray-500">
                  {[equipe.categorie, equipe.discipline, equipe.engagement].filter(Boolean).join(' · ')}
                </p>
              </header>

              {equipe.encadrants?.length > 0 && (
                <Section titre="Encadrement" gens={equipe.encadrants} ton="primary" />
              )}

              <Section
                titre={`Joueurs (${equipe.effectif})`}
                gens={equipe.joueurs}
                ton="neutre"
              />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

/** Une section de la carte : un intertitre, puis des pastilles. */
function Section({ titre, gens, ton }: { titre: string; gens: any[]; ton: 'primary' | 'neutre' }) {
  if (gens.length === 0) {
    return null;
  }

  const couleurs =
    ton === 'primary'
      ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';

  return (
    <div>
      <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-2">{titre}</h3>
      <ul className="flex flex-wrap gap-1.5">
        {gens.map((personne, rang) => (
          <li
            key={rang}
            className={`inline-flex items-center gap-1.5 rounded-full text-sm ${couleurs} ${
              personne.photo ? 'pl-1 pr-3 py-1' : 'px-3 py-1'
            }`}
          >
            {/* Le portrait quand le club a choisi de le publier : il tient
                dans la pastille, à la place du numéro. */}
            {personne.photo && (
              <img
                src={personne.photo}
                alt=""
                loading="lazy"
                className="w-7 h-7 rounded-full object-cover bg-white/60"
              />
            )}
            {personne.numero && (
              <span className="font-bold tabular-nums opacity-70">{personne.numero}</span>
            )}
            {personne.nom}
            {personne.role && personne.role !== 'Joueur' && (
              <span className="text-xs opacity-70">· {personne.role}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
